import React, { useState } from 'react';
import { StatCard } from '../components/common/StatCard';
import { ApprenticeUnitSummary } from '../components/units/ApprenticeUnitSummary';
import { buildLevels } from '../lib/helpers';

const MONTHLY_TS_TARGET = 12; // hours per month expected at trade school

function getApprMetrics(appr, units, trainingPlans, workplaceSignoffs, knowledgeUnits, evidence, tradeSchoolLogs) {
  const plan = trainingPlans.filter(p => p.apprentice_id === appr.id);
  const levels = buildLevels(units, plan);
  const totalUnits = levels.reduce((s, l) => s + l.units.length, 0);
  const completedUnits = levels.reduce((s, l) => s + l.units.filter(u => {
    const ws = workplaceSignoffs.find(s => s.apprentice_id === appr.id && s.unit_code === u.code);
    const ku = knowledgeUnits.find(k => k.apprentice_id === appr.id && k.unit_code === u.code);
    return !!ws && !!ku;
  }).length, 0);

  // Time calculations
  const now = new Date();
  const startDate = appr.start_date ? new Date(appr.start_date) : null;
  const endDate = startDate ? new Date(new Date(startDate).setFullYear(startDate.getFullYear() + 3)) : null;
  const totalDays = startDate && endDate ? (endDate - startDate) / (1000 * 60 * 60 * 24) : 0;
  const elapsedDays = startDate ? Math.max(0, (now - startDate) / (1000 * 60 * 60 * 24)) : 0;
  const daysRemaining = endDate ? Math.max(0, Math.round((endDate - now) / (1000 * 60 * 60 * 24))) : null;
  const timePct = totalDays > 0 ? Math.min(100, Math.round((elapsedDays / totalDays) * 100)) : 0;
  const unitPct = totalUnits > 0 ? Math.round((completedUnits / totalUnits) * 100) : 0;

  // Months elapsed
  const monthsElapsed = startDate ? Math.max(0, (now.getFullYear() - startDate.getFullYear()) * 12 + (now.getMonth() - startDate.getMonth())) : 0;

  // Trade school
  const tsLogs = tradeSchoolLogs.filter(l => l.apprentice_id === appr.id);
  const tsActual = Math.round(tsLogs.reduce((a, l) => a + Number(l.hours || 0), 0) * 100) / 100;
  const tsExpected = monthsElapsed * MONTHLY_TS_TARGET;
  const tsPct = tsExpected > 0 ? Math.round((tsActual / tsExpected) * 100) : (tsActual > 0 ? 100 : 0);

  // Evidence activity
  const apprEvidence = evidence.filter(e => e.apprentice_id === appr.id);
  const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
  const recentEvidence = apprEvidence.filter(e => new Date(e.created_at || e.date) > thirtyDaysAgo).length;
  const lastEvDate = apprEvidence.length > 0
    ? apprEvidence.reduce((latest, e) => {
        const d = new Date(e.created_at || e.date);
        return d > latest ? d : latest;
      }, new Date(0))
    : null;
  const daysSinceEvidence = lastEvDate ? Math.round((now - lastEvDate) / (1000 * 60 * 60 * 24)) : null;

  // Traffic light: compare unit progress to time progress
  const gap = timePct - unitPct;
  let status = 'green';
  if (!startDate) status = 'grey';
  else if (gap > 30) status = 'red';
  else if (gap > 15) status = 'amber';

  // Trade school traffic light
  let tsStatus = 'green';
  if (tsExpected === 0 && tsActual === 0) tsStatus = 'grey';
  else if (tsPct < 60) tsStatus = 'red';
  else if (tsPct < 85) tsStatus = 'amber';

  // Evidence traffic light
  let evStatus = 'green';
  if (daysSinceEvidence === null) evStatus = 'grey';
  else if (daysSinceEvidence > 30) evStatus = 'red';
  else if (daysSinceEvidence > 14) evStatus = 'amber';

  return {
    appr,
    totalUnits, completedUnits, unitPct,
    timePct, daysRemaining,
    tsActual, tsExpected, tsPct, tsStatus,
    recentEvidence, daysSinceEvidence, evStatus,
    status,
    levels,
    // For sorting
    sortScore: status === 'red' ? 0 : status === 'amber' ? 1 : status === 'grey' ? 3 : 2,
  };
}

function StatusDot({ status }) {
  const colors = { red: '#ef4444', amber: '#f59e0b', green: '#22c55e', grey: '#6b7280' };
  return (
    <span style={{
      display: 'inline-block', width: 10, height: 10, borderRadius: '50%',
      background: colors[status] || colors.grey, flexShrink: 0,
    }} />
  );
}

function ProgressBar({ pct, color, bgColor }) {
  return (
    <div style={{ height: 6, borderRadius: 3, background: bgColor || 'var(--border)', width: '100%', minWidth: 60 }}>
      <div style={{ height: '100%', borderRadius: 3, background: color || 'var(--accent)', width: `${Math.min(100, pct)}%`, transition: 'width .3s' }} />
    </div>
  );
}

export function DashboardPage({ profile, evidence, myApprentices, selApprId, setSelApprId, allProfiles, units, trainingPlans, signoffs, workplaceSignoffs, knowledgeUnits, tradeSchoolLogs, sites, getSiteName, onAddEvidence, getUnitName }) {
  const isApprentice = profile.role === 'apprentice';
  const [sortCol, setSortCol] = useState('status');
  const [sortDir, setSortDir] = useState('asc');
  const [siteFilter, setSiteFilter] = useState('all');
  const [breakdownId, setBreakdownId] = useState(null);

  function handleSort(col) {
    if (sortCol === col) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortCol(col);
      setSortDir('asc');
    }
  }

  function SortHeader({ col, children }) {
    const active = sortCol === col;
    return (
      <th onClick={() => handleSort(col)} style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>
        {children} {active ? (sortDir === 'asc' ? '▲' : '▼') : ''}
      </th>
    );
  }

  // ── Apprentice's own dashboard ──
  if (isApprentice) {
    const activeId = profile.id;
    const myEv = evidence.filter(e => e.apprentice_id === activeId);
    const approved = myEv.filter(e => e.status === 'approved').length;
    const pending = myEv.filter(e => e.status === 'pending').length;
    const rejected = myEv.filter(e => e.status === 'rejected').length;
    const plan = trainingPlans.filter(p => p.apprentice_id === activeId);
    const levels = buildLevels(units, plan);
    const totalUnits = levels.reduce((s, l) => s + l.units.length, 0);
    const completedUnits = levels.reduce((s, l) => s + l.units.filter(u => {
      const ws = workplaceSignoffs.find(s => s.apprentice_id === activeId && s.unit_code === u.code);
      const ku = knowledgeUnits.find(k => k.apprentice_id === activeId && k.unit_code === u.code);
      return !!ws && !!ku;
    }).length, 0);

    return (
      <div>
        <div className="page-hdr">
          <div>
            <div className="page-title">Dashboard</div>
            <div className="page-sub">Welcome back, {profile.name?.split(' ')[0]}</div>
          </div>
          <button className="btn" onClick={() => onAddEvidence()}>+ Log Evidence</button>
        </div>

        <div className="stats">
          <StatCard value={approved} label="Approved" accent="g" />
          <StatCard value={pending} label="Pending" accent="a" />
          <StatCard value={`${completedUnits}/${totalUnits}`} label="Units Done" accent="b" />
          <StatCard value={rejected} label="Returned" />
        </div>

        {rejected > 0 && (
          <div style={{ padding: '10px 14px', borderRadius: 6, background: 'rgba(192,57,43,.08)', border: '1px solid rgba(192,57,43,.3)', fontSize: 12.5, marginBottom: 14 }}>
            <strong style={{ color: 'var(--red)' }}>{rejected} {rejected === 1 ? 'entry needs' : 'entries need'} fixing.</strong>{' '}
            Open the Evidence Log to see what your trainer asked for.
          </div>
        )}

        {plan.length === 0 ? (
          <div className="empty">
            <div className="empty-icon">📐</div>
            <p>No training plan loaded yet</p>
            <p style={{ fontSize: 12, color: 'var(--muted)', marginTop: 6 }}>
              Your employer or trainer needs to import your training plan before your
              units show up here.
            </p>
          </div>
        ) : (
          <>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', letterSpacing: 0.5, margin: '18px 0 8px' }}>
              YOUR UNITS — OPEN A YEAR, THEN TAP A UNIT TO SEE WHAT IT NEEDS
            </div>
            <ApprenticeUnitSummary
              levels={levels}
              apprenticeId={activeId}
              evidence={evidence}
              signoffs={signoffs}
              workplaceSignoffs={workplaceSignoffs}
              knowledgeUnits={knowledgeUnits}
              onAddEvidence={onAddEvidence}
              canLog
            />
          </>
        )}
      </div>
    );
  }

  // ── Employer / Trainer cohort dashboard ──
  const allMetrics = myApprentices.map(a =>
    getApprMetrics(a, units, trainingPlans, workplaceSignoffs, knowledgeUnits, evidence, tradeSchoolLogs || [])
  );

  // Site filter
  const hasSites = (sites || []).length > 0;
  const metrics = siteFilter === 'all'
    ? allMetrics
    : siteFilter === 'unassigned'
      ? allMetrics.filter(m => !m.appr.site_id)
      : allMetrics.filter(m => m.appr.site_id === siteFilter);

  // Sort
  const sorted = [...metrics].sort((a, b) => {
    let va, vb;
    switch (sortCol) {
      case 'status': va = a.sortScore; vb = b.sortScore; break;
      case 'name': va = a.appr.name?.toLowerCase(); vb = b.appr.name?.toLowerCase(); return sortDir === 'asc' ? (va < vb ? -1 : 1) : (va > vb ? -1 : 1);
      case 'progress': va = a.unitPct; vb = b.unitPct; break;
      case 'time': va = a.timePct; vb = b.timePct; break;
      case 'ts': va = a.tsPct; vb = b.tsPct; break;
      case 'evidence': va = a.daysSinceEvidence ?? 999; vb = b.daysSinceEvidence ?? 999; break;
      case 'remaining': va = a.daysRemaining ?? 9999; vb = b.daysRemaining ?? 9999; break;
      default: va = a.sortScore; vb = b.sortScore;
    }
    return sortDir === 'asc' ? va - vb : vb - va;
  });

  // Summary stats (from filtered)
  const atRisk = metrics.filter(m => m.status === 'red').length;
  const behind = metrics.filter(m => m.status === 'amber').length;
  const onTrack = metrics.filter(m => m.status === 'green').length;
  const totalPending = evidence.filter(e => e.status === 'pending').length;

  return (
    <div>
      <div className="page-hdr">
        <div>
          <div className="page-title">Dashboard</div>
          <div className="page-sub">Apprentice cohort overview — {metrics.length} apprentice{metrics.length !== 1 ? 's' : ''}{siteFilter !== 'all' && ` (filtered)`}</div>
        </div>
        {hasSites && (
          <select
            className="fsel"
            value={siteFilter}
            onChange={e => setSiteFilter(e.target.value)}
            style={{ fontSize: 12, padding: '4px 10px', minWidth: 140 }}
          >
            <option value="all">All Sites</option>
            {(sites || []).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            <option value="unassigned">Unassigned</option>
          </select>
        )}
      </div>

      {/* Summary stats */}
      <div className="stats">
        <StatCard value={onTrack} label="On Track" accent="g" />
        <StatCard value={behind} label="Behind" accent="a" />
        <StatCard value={atRisk} label="At Risk" accent="r" />
        <StatCard value={totalPending} label="Pending Review" accent="b" />
      </div>

      {/* Workplace sign-off alerts for supervisors (and employers) */}
      {(profile.role === 'supervisor' || profile.role === 'employer') && (() => {
        // Find units where Knowledge + Evidence are done but Workplace is not
        const readyUnits = [];
        myApprentices.forEach(a => {
          const plan = trainingPlans.filter(p => p.apprentice_id === a.id);
          plan.forEach(p => {
            const ku = knowledgeUnits.find(k => k.apprentice_id === a.id && k.unit_code === p.unit_code);
            const es = signoffs.find(s => s.apprentice_id === a.id && s.unit_code === p.unit_code);
            const ws = workplaceSignoffs.find(w => w.apprentice_id === a.id && w.unit_code === p.unit_code);
            if (ku && es && !ws) {
              const unit = units.find(u => u.code === p.unit_code);
              readyUnits.push({
                apprentice: a.name,
                apprenticeId: a.id,
                unitCode: p.unit_code,
                unitName: unit?.name || p.unit_code,
              });
            }
          });
        });

        if (readyUnits.length === 0) return null;

        return (
          <div className="card" style={{ marginBottom: 20, borderLeft: '3px solid var(--accent)' }}>
            <div className="card-hdr">
              <span className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 18 }}>🔔</span>
                Ready for Workplace Sign-off
                <span style={{
                  background: 'var(--accent)', color: '#fff', borderRadius: 100,
                  padding: '1px 8px', fontSize: 11, fontWeight: 700, marginLeft: 4,
                }}>{readyUnits.length}</span>
              </span>
            </div>
            <div style={{ padding: '4px 16px 12px' }}>
              <p style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 10 }}>
                These units have Knowledge and Evidence signed off — they just need your Workplace confirmation.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {readyUnits.map((r, i) => (
                  <div key={i} style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    background: 'rgba(245,166,35,.05)', border: '1px solid rgba(245,166,35,.12)',
                    borderRadius: 6, padding: '8px 12px', flexWrap: 'wrap',
                  }}>
                    <span style={{
                      background: 'var(--accent)', color: '#fff', borderRadius: 4,
                      padding: '2px 7px', fontSize: 10, fontWeight: 700, flexShrink: 0,
                    }}>{r.unitCode}</span>
                    <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--head)' }}>{r.unitName}</span>
                    <span style={{ fontSize: 11, color: 'var(--muted)', marginLeft: 'auto' }}>{r.apprentice}</span>
                    <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                      <span style={{ fontSize: 9, background: 'rgba(39,174,96,.1)', color: 'var(--green)', padding: '2px 6px', borderRadius: 3, fontWeight: 600 }}>Knowledge ✓</span>
                      <span style={{ fontSize: 9, background: 'rgba(39,174,96,.1)', color: 'var(--green)', padding: '2px 6px', borderRadius: 3, fontWeight: 600 }}>Evidence ✓</span>
                      <span style={{ fontSize: 9, background: 'rgba(245,166,35,.1)', color: 'var(--accent)', padding: '2px 6px', borderRadius: 3, fontWeight: 600 }}>Workplace ⏳</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Cohort table */}
      <div className="card">
        <div className="card-hdr">
          <span className="card-title">Apprentice Progress</span>
          <span style={{ fontSize: 11, color: 'var(--muted)' }}>Click column headers to sort</span>
        </div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <SortHeader col="status">Status</SortHeader>
                <SortHeader col="name">Apprentice</SortHeader>
                <SortHeader col="progress">Unit Progress</SortHeader>
                <SortHeader col="time">Time Used</SortHeader>
                <SortHeader col="ts">Trade School</SortHeader>
                <SortHeader col="evidence">Evidence Activity</SortHeader>
                <SortHeader col="remaining">Days Left</SortHeader>
              </tr>
            </thead>
            <tbody>
              {sorted.length === 0 ? (
                <tr><td colSpan={7} style={{ textAlign: 'center', color: 'var(--muted)', padding: 40 }}>No apprentices linked yet</td></tr>
              ) : sorted.map(m => (
                <tr key={m.appr.id} style={m.status === 'red' ? { background: 'rgba(239,68,68,.06)' } : m.status === 'amber' ? { background: 'rgba(245,158,11,.04)' } : undefined}>
                  {/* Status */}
                  <td style={{ textAlign: 'center' }}>
                    <StatusDot status={m.status} />
                  </td>

                  {/* Name */}
                  <td>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{m.appr.name}</div>
                    <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 2 }}>
                      {getSiteName && getSiteName(m.appr.site_id) && (
                        <span style={{ background: 'var(--panel)', padding: '1px 5px', borderRadius: 3, marginRight: 4, fontSize: 9, border: '1px solid var(--border)' }}>
                          {getSiteName(m.appr.site_id)}
                        </span>
                      )}
                      {m.appr.start_date && (
                        <span>Started {new Date(m.appr.start_date).toLocaleDateString('en-AU', { month: 'short', year: 'numeric' })}</span>
                      )}
                    </div>
                  </td>

                  {/* Unit Progress */}
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <ProgressBar
                        pct={m.unitPct}
                        color={m.status === 'red' ? '#ef4444' : m.status === 'amber' ? '#f59e0b' : 'var(--green)'}
                      />
                      <span style={{ fontSize: 12, fontWeight: 700, fontFamily: 'var(--font-d)', minWidth: 32, color: m.status === 'red' ? '#ef4444' : m.status === 'amber' ? '#f59e0b' : 'var(--green)' }}>
                        {m.unitPct}%
                      </span>
                    </div>
                    <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 2 }}>
                      {m.completedUnits}/{m.totalUnits} units
                    </div>
                  </td>

                  {/* Time Used */}
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <ProgressBar pct={m.timePct} color="var(--accent2)" />
                      <span style={{ fontSize: 12, fontWeight: 700, fontFamily: 'var(--font-d)', minWidth: 32, color: 'var(--accent2)' }}>
                        {m.timePct}%
                      </span>
                    </div>
                  </td>

                  {/* Trade School */}
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <StatusDot status={m.tsStatus} />
                      <span style={{ fontSize: 12, fontWeight: 600 }}>
                        {m.tsActual}h
                      </span>
                      <span style={{ fontSize: 10, color: 'var(--muted)' }}>
                        / {m.tsExpected}h
                      </span>
                    </div>
                    {m.tsExpected > 0 && (
                      <div style={{ fontSize: 10, color: m.tsStatus === 'red' ? '#ef4444' : m.tsStatus === 'amber' ? '#f59e0b' : 'var(--muted)', marginTop: 2 }}>
                        {m.tsPct >= 100 ? 'On target' : `${100 - m.tsPct}% behind`}
                      </div>
                    )}
                  </td>

                  {/* Evidence Activity */}
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <StatusDot status={m.evStatus} />
                      <span style={{ fontSize: 12, fontWeight: 600 }}>
                        {m.recentEvidence} <span style={{ fontWeight: 400, color: 'var(--muted)' }}>last 30d</span>
                      </span>
                    </div>
                    <div style={{ fontSize: 10, color: m.evStatus === 'red' ? '#ef4444' : 'var(--muted)', marginTop: 2 }}>
                      {m.daysSinceEvidence === null ? 'No evidence yet' :
                       m.daysSinceEvidence === 0 ? 'Active today' :
                       `${m.daysSinceEvidence}d since last entry`}
                    </div>
                  </td>

                  {/* Days Remaining */}
                  <td>
                    {m.daysRemaining !== null ? (
                      <div style={{
                        fontSize: 14, fontWeight: 900, fontFamily: 'var(--font-d)',
                        color: m.daysRemaining < 180 ? '#ef4444' : m.daysRemaining < 365 ? '#f59e0b' : 'var(--head)',
                      }}>
                        {m.daysRemaining}
                      </div>
                    ) : (
                      <span style={{ fontSize: 12, color: 'var(--muted)' }}>No date</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Per-apprentice unit breakdown — what's outstanding, unit by unit */}
      {sorted.length > 0 && (() => {
        const chosen = sorted.find(m => m.appr.id === breakdownId) || sorted[0];
        return (
          <div style={{ marginTop: 30 }}>
            {/* Deliberately loud: this picker drives everything below it, and as a
                small grey dropdown it disappeared into the page. */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap',
              padding: '16px 18px', marginBottom: 14, borderRadius: 6,
              background: 'var(--surface)', border: '1px solid var(--border)',
              borderLeft: '4px solid var(--accent)',
            }}>
              <div style={{ flex: '1 1 220px' }}>
                <div style={{
                  fontFamily: 'var(--font-d)', fontWeight: 900, fontSize: 18, letterSpacing: 1,
                  textTransform: 'uppercase', color: 'var(--head)',
                }}>
                  Unit Breakdown
                </div>
                <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 3 }}>
                  Open a year, then tap a unit to see what its evidence has to show.
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                <label htmlFor="breakdown-appr" style={{
                  fontSize: 10.5, fontWeight: 700, letterSpacing: 0.8,
                  textTransform: 'uppercase', color: 'var(--accent)',
                }}>
                  Showing apprentice
                </label>
                <select
                  id="breakdown-appr"
                  className="fsel"
                  value={chosen.appr.id}
                  onChange={e => setBreakdownId(e.target.value)}
                  style={{
                    fontSize: 16, fontWeight: 700, padding: '10px 14px', minWidth: 250,
                    border: '2px solid var(--accent)', color: 'var(--head)',
                    background: 'var(--bg)', borderRadius: 6, cursor: 'pointer',
                  }}
                >
                  {sorted.map(m => <option key={m.appr.id} value={m.appr.id}>{m.appr.name}</option>)}
                </select>
              </div>
            </div>
            <ApprenticeUnitSummary
              levels={chosen.levels}
              apprenticeId={chosen.appr.id}
              evidence={evidence}
              signoffs={signoffs}
              workplaceSignoffs={workplaceSignoffs}
              knowledgeUnits={knowledgeUnits}
            />
          </div>
        );
      })()}

      {/* Legend */}
      <div style={{ display: 'flex', gap: 20, marginTop: 12, fontSize: 11, color: 'var(--muted)', flexWrap: 'wrap' }}>
        <span><StatusDot status="green" /> On track — progress ahead of or matching time</span>
        <span><StatusDot status="amber" /> Behind — progress 15-30% behind time</span>
        <span><StatusDot status="red" /> At risk — progress 30%+ behind time</span>
        <span><StatusDot status="grey" /> No start date set</span>
      </div>
    </div>
  );
}
